import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { LangProvider } from './contexts/LangContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { OfflineBanner } from './components/OfflineBanner';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { trackPageView } from './lib/analytics';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import AuthCallback from './pages/AuthCallback';
import CoursesPage from './pages/CoursesPage';
import "@/App.css";

// Code-split: heavy pages loaded on demand
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const CourseDetailPage = lazy(() => import('./pages/CourseDetailPage'));
const MentorWaitlistPage = lazy(() => import('./pages/MentorWaitlistPage'));
const JobsPage = lazy(() => import('./pages/JobsPage'));
const FieldGuidePage = lazy(() => import('./pages/FieldGuidePage'));
const SuggestionsPage = lazy(() => import('./pages/SuggestionsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const CurriculumPage = lazy(() => import('./pages/CurriculumPage'));
const EcosystemPage = lazy(() => import('./pages/EcosystemPage'));
const SponsorPage = lazy(() => import('./pages/SponsorPage'));
const FoundingCirclePage = lazy(() => import('./pages/FoundingCirclePage'));
const ChallengesPage = lazy(() => import('./pages/ChallengesPage'));
const LibraryPage = lazy(() => import('./pages/LibraryPage'));

// Code-split: employer pages (separate audience)
const EmployerPricingPage = lazy(() => import('./pages/EmployerPricingPage'));
const EmployerDashboardPage = lazy(() => import('./pages/EmployerDashboardPage'));
const EmployersLandingPage = lazy(() => import('./pages/EmployersLandingPage'));
const EmployerAuthPage = lazy(() => import('./pages/EmployerAuthPage'));

// Code-split: admin pages (owner-only)
const AdminDashboardMain = lazy(() => import('./pages/admin/AdminDashboardMain'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminCoursesPage = lazy(() => import('./pages/admin/AdminCoursesPage'));
const AdminEmployersPage = lazy(() => import('./pages/admin/AdminEmployersPage'));
const AdminTrafficPage = lazy(() => import('./pages/admin/AdminTrafficPage'));
const AdminGracePage = lazy(() => import('./pages/admin/AdminGracePage'));
const AdminSupportPage = lazy(() => import('./pages/admin/AdminSupportPage'));
const AdminSystemPage = lazy(() => import('./pages/admin/AdminSystemPage'));
const AdminTractionPage = lazy(() => import('./pages/admin/AdminTractionPage'));

// Lazy-load Grace widget
const GraceWidget = lazy(() => import('./components/GraceWidget').then(m => ({ default: m.GraceWidget })));

function LazyFallback() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="w-8 h-8 border-2 border-[#D4A843]/30 border-t-[#D4A843] rounded-full animate-spin" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();

  // Track every page view
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  if (location.hash?.includes('session_id=') || location.hash?.includes('token=')) {
    return <AuthCallback />;
  }

  const isAdmin = location.pathname.startsWith('/admin');
  if (isAdmin) {
    return (
      <ErrorBoundary level="route">
        <Suspense fallback={<LazyFallback />}>
          <Routes>
            <Route path="/admin/dashboard" element={<AdminDashboardMain />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/courses" element={<AdminCoursesPage />} />
            <Route path="/admin/employers" element={<AdminEmployersPage />} />
            <Route path="/admin/traffic" element={<AdminTrafficPage />} />
            <Route path="/admin/grace" element={<AdminGracePage />} />
            <Route path="/admin/support" element={<AdminSupportPage />} />
            <Route path="/admin/system" element={<AdminSystemPage />} />
            <Route path="/admin/traction" element={<AdminTractionPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <ErrorBoundary level="route">
          <Suspense fallback={<LazyFallback />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/auth/employer" element={<EmployerAuthPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/courses/:id" element={<CourseDetailPage />} />
              <Route path="/mentor-waitlist" element={<MentorWaitlistPage />} />
              <Route path="/jobs" element={<JobsPage />} />
              <Route path="/field-guide" element={<FieldGuidePage />} />
              <Route path="/suggestions" element={<SuggestionsPage />} />
              <Route path="/employers/pricing" element={<EmployerPricingPage />} />
              <Route path="/employers/dashboard" element={<EmployerDashboardPage />} />
              <Route path="/employers" element={<EmployersLandingPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/curriculum" element={<CurriculumPage />} />
              <Route path="/ecosystem" element={<EcosystemPage />} />
              <Route path="/sponsor" element={<SponsorPage />} />
              <Route path="/founding-circle" element={<FoundingCirclePage />} />
              <Route path="/challenges" element={<ChallengesPage />} />
              <Route path="/library" element={<LibraryPage />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
      <Footer />
    </div>
  );
}

function GraceWidgetWrapper() {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;
  return (
    <Suspense fallback={null}>
      <GraceWidget />
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary level="global">
      <HelmetProvider>
        <BrowserRouter>
          <LangProvider>
            <AuthProvider>
              <OfflineBanner />
              <AppRouter />
              <GraceWidgetWrapper />
            </AuthProvider>
          </LangProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
